import React, { useEffect, useState, useContext } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from '../../axiosConfig';
import TrainerLayout from './TrainerLayout';
import { CustomDatePicker } from '../commons/datepicker';
import '../../styles/SurveyDetails.css';
import { TrainerContext } from '../../context/TrainerContext';

const SurveyDetails = () => {
    const { surveyId, dogId } = useParams();
    const navigate = useNavigate();
    const { trainer } = useContext(TrainerContext);
    const { t } = useTranslation(['surveys', 'common']);
    const [survey, setSurvey] = useState(null);
    const [dog, setDog] = useState(null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(true);
    const [responses, setResponses] = useState({});
    const [existingResponses, setExistingResponses] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);

                // Only proceed if surveyId is valid
                if (!surveyId || surveyId === 'undefined') {
                    setError(t('surveys:invalidSurveyId'));
                    setLoading(false);
                    return;
                }

                // Fetch survey details
                const surveyResponse = await axios.get(`/api/v1/survey/${surveyId}`, {
                    headers: { Authorization: `Bearer ${localStorage.getItem('jwt')}` },
                });
                setSurvey(surveyResponse.data);

                // If dogId is provided and valid, fetch dog details and existing responses
                if (dogId && dogId !== 'undefined') {
                    const dogResponse = await axios.get(`/api/v1/dogs/${dogId}`, {
                        headers: { Authorization: `Bearer ${localStorage.getItem('jwt')}` },
                    });
                    setDog(dogResponse.data);

                    // Fetch existing responses if any
                    try {
                        const responsesResponse = await axios.get(`/api/v1/survey-response/dog/${dogId}/survey/${surveyId}`, {
                            headers: { Authorization: `Bearer ${localStorage.getItem('jwt')}` },
                        });

                        setExistingResponses(responsesResponse.data);

                        // Pre-populate form with existing responses
                        const responseMap = {};
                        responsesResponse.data.forEach(response => {
                            const question = surveyResponse.data.questions.find(
                                q => q.id === response.questionId
                            );

                            if (!question) return;

                            if (question.questionType === 'MULTIPLE_CHOICE') {
                                responseMap[response.questionId] = response.multipleChoiceAnswer || [];
                            } else if (question.questionType === 'DATE') {
                                responseMap[response.questionId] = response.dateAnswer || '';
                            } else if (question.questionType === 'SINGLE_CHOICE') {
                                responseMap[response.questionId] = response.singleChoiceAnswer || '';
                            } else if (question.questionType === 'CHOICE_WITH_TEXT') {
                                responseMap[response.questionId] = {
                                    selectedOption: response.singleChoiceAnswer || '',
                                    customText: response.textAnswer || ''
                                };
                            } else {
                                responseMap[response.questionId] = response.textAnswer || '';
                            }
                        });

                        setResponses(responseMap);
                    } catch (err) {
                        // It's OK if there are no existing responses
                        console.log('No existing responses found');
                    }
                }
            } catch (err) {
                console.error('Error fetching data:', err);
                setError(t('surveys:fetchDataError'));
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [surveyId, dogId]);

    const handleResponseChange = (questionId, value) => {
        setResponses(prev => ({ ...prev, [questionId]: value }));
    };

    const handleMultipleChoiceChange = (questionId, option) => {
        setResponses(prev => {
            // Initialize as empty array if not exists or not an array
            const current = Array.isArray(prev[questionId]) ? prev[questionId] : [];

            return {
                ...prev,
                [questionId]: current.includes(option)
                    ? current.filter(o => o !== option)
                    : [...current, option]
            };
        });
    };

    const validateResponses = () => {
        // Check if all required questions have answers
        for (const question of survey.questions) {
            const response = responses[question.id];

            if (!response && question.required) {
                alert(t('surveys:answerRequired', { question: question.questionText }));
                return false;
            }

            if (question.questionType === 'MULTIPLE_CHOICE' &&
                (!response || response.length === 0) &&
                question.required) {
                alert(t('surveys:selectAtLeastOne', { question: question.questionText }));
                return false;
            }
        }

        return true;
    };

    const handleSubmit = async () => {
        if (!dogId || dogId === 'undefined') {
            alert(t('surveys:noDogId'));
            return;
        }

        if (!validateResponses()) {
            return;
        }

        setIsSubmitting(true);

        // Prepare responses in the format expected by the backend
        const responseData = Object.keys(responses).map(questionId => {
            const question = survey.questions.find(q => q.id === questionId);
            if (!question) {
                console.error(`Question with ID ${questionId} not found in survey`);
                return null;
            }

            const response = responses[questionId];

            if (question.questionType === 'CHOICE_WITH_TEXT') {
                return {
                    questionId,
                    textAnswer: response.customText || null,
                    dateAnswer: null,
                    singleChoiceAnswer: response.selectedOption || null,
                    multipleChoiceAnswer: null
                };
            } else {
                return {
                    questionId,
                    textAnswer: question.questionType === 'TEXT' ? response : null,
                    dateAnswer: question.questionType === 'DATE' ? response : null,
                    singleChoiceAnswer: question.questionType === 'SINGLE_CHOICE' ? response : null,
                    multipleChoiceAnswer: question.questionType === 'MULTIPLE_CHOICE' ? response : null
                };
            }
        }).filter(response => response !== null);

        const submitData = {
            surveyId,
            dogId,
            responses: responseData
        };

        try {
            await axios.post('/api/v1/survey-response/submit', submitData, {
                headers: { Authorization: `Bearer ${localStorage.getItem('jwt')}` },
            });

            alert(t('surveys:responsesSaved'));

            // Redirect back to dog profile or another appropriate page
            if (dogId && dogId !== 'undefined') {
                navigate(`/dogs/${dogId}`);
            } else {
                navigate(-1);
            }
        } catch (err) {
            console.error('Error submitting responses:', err);
            alert(t('surveys:saveError'));
        } finally {
            setIsSubmitting(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        // Format yyyy-MM-dd for input[type="date"]
        return dateString.split('T')[0];
    };

    if (loading) {
        return (
            <TrainerLayout>
                <div className="survey-container">
                    <div className="loading-state">
                        <p>{t('surveys:loadingSurvey')}</p>
                    </div>
                </div>
            </TrainerLayout>
        );
    }

    if (error) {
        return (
            <TrainerLayout>
                <div className="survey-container">
                    <div className="error-state">
                        <h2>{t('surveys:errorOccurred')}</h2>
                        <p>{error}</p>
                        <button onClick={() => navigate(-1)} className="back-btn">
                            {t('surveys:back')}
                        </button>
                    </div>
                </div>
            </TrainerLayout>
        );
    }

    if (!survey) {
        return (
            <TrainerLayout>
                <div className="survey-container">
                    <div className="error-state">
                        <h2>{t('surveys:notFound')}</h2>
                        <button onClick={() => navigate(-1)} className="back-btn">
                            {t('surveys:back')}
                        </button>
                    </div>
                </div>
            </TrainerLayout>
        );
    }

    return (
        <TrainerLayout>
            <div className="survey-container">
                <div className="survey-header">
                    <h1 className="survey-title">{survey.title}</h1>
                    {dog && (
                        <div className="dog-info">
                            <span>{t('surveys:surveyFor')} </span>
                            <strong>{dog.name}</strong>
                        </div>
                    )}
                </div>

                <div className="survey-content">
                    {survey.questions
                        .sort((a, b) => a.questionIndex - b.questionIndex)
                        .map((question, index) => (
                            <div key={question.id} className="question-block">
                                <div className="question-header">
                                    <label className="question-label">
                                        <span className="question-number">{index + 1}.</span>
                                        {question.questionText}
                                        {question.required && <span className="required-mark">*</span>}
                                    </label>
                                </div>

                                <div className="question-body">
                                    {question.questionType === "TEXT" && (
                                        <input
                                            type="text"
                                            className="survey-input"
                                            placeholder={t('surveys:typeAnswer')}
                                            value={responses[question.id] || ""}
                                            onChange={(e) => handleResponseChange(question.id, e.target.value)}
                                        />
                                    )}

                                    {question.questionType === "DATE" && (
                                        <CustomDatePicker
                                            name={`survey-date-${question.id}`}
                                            value={formatDate(responses[question.id]) || ""}
                                            onChange={(e) => handleResponseChange(question.id, e.target.value)}
                                        />
                                    )}

                                    {question.questionType === "SINGLE_CHOICE" && (
                                        <div className="single-choice">
                                            {question.answerOptions && question.answerOptions.length > 0 ? (
                                                question.answerOptions.map((option, idx) => (
                                                    <label key={idx} className="radio-container">
                                                        <input
                                                            type="radio"
                                                            name={`single-${question.id}`}
                                                            value={option}
                                                            checked={responses[question.id] === option}
                                                            onChange={() => handleResponseChange(question.id, option)}
                                                        />
                                                        <span className="option-text">{option}</span>
                                                    </label>
                                                ))
                                            ) : (
                                                <p className="no-options">{t('surveys:noOptions')}</p>
                                            )}
                                        </div>
                                    )}

                                    {question.questionType === "MULTIPLE_CHOICE" && (
                                        <div className="multi-choice">
                                            {question.answerOptions && question.answerOptions.length > 0 ? (
                                                question.answerOptions.map((option, idx) => (
                                                    <label key={idx} className="checkbox-container">
                                                        <input
                                                            type="checkbox"
                                                            value={option}
                                                            checked={Array.isArray(responses[question.id]) && responses[question.id].includes(option)}
                                                            onChange={() => handleMultipleChoiceChange(question.id, option)}
                                                        />
                                                        <span className="option-text">{option}</span>
                                                    </label>
                                                ))
                                            ) : (
                                                <p className="no-options">{t('surveys:noOptions')}</p>
                                            )}
                                        </div>
                                    )}

                                    {question.questionType === "CHOICE_WITH_TEXT" && (
                                        <div className="choice-with-text">
                                            {question.answerOptions && question.answerOptions.length > 0 ? (
                                                <>
                                                    <div className="options-section">
                                                        {question.answerOptions.map((option, idx) => (
                                                            <label key={idx} className="radio-container">
                                                                <input
                                                                    type="radio"
                                                                    name={`choice-with-text-${question.id}`}
                                                                    value={option}
                                                                    checked={responses[question.id]?.selectedOption === option}
                                                                    onChange={() => handleResponseChange(question.id, {
                                                                        selectedOption: option,
                                                                        customText: responses[question.id]?.customText || ''
                                                                    })}
                                                                />
                                                                <span className="option-text">{option}</span>
                                                            </label>
                                                        ))}
                                                    </div>
                                                    <div className="text-section">
                                                        <input
                                                            type="text"
                                                            className="survey-input"
                                                            placeholder={t('surveys:orTypeOwnAnswer')}
                                                            value={responses[question.id]?.customText || ""}
                                                            onChange={(e) => handleResponseChange(question.id, {
                                                                selectedOption: responses[question.id]?.selectedOption || '',
                                                                customText: e.target.value
                                                            })}
                                                        />
                                                    </div>
                                                </>
                                            ) : (
                                                <input
                                                    type="text"
                                                    className="survey-input"
                                                    placeholder={t('surveys:typeAnswer')}
                                                    value={responses[question.id]?.customText || ""}
                                                    onChange={(e) => handleResponseChange(question.id, {
                                                        customText: e.target.value
                                                    })}
                                                />
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}

                    {dogId && (
                        <div className="form-actions">
                            <button
                                onClick={() => navigate(-1)}
                                className="cancel-btn"
                            >
                                {t('common:cancel')}
                            </button>
                            <button
                                onClick={handleSubmit}
                                className="save-responses-btn"
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? t('surveys:submitting') : t('surveys:saveResponses')}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </TrainerLayout>
    );
};

export default SurveyDetails;
